import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Link,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Stack
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const REGISTRATION_STORAGE_KEY = 'supplier_registration_progress';

function SupplierRegister() {
  const { _reinitializeAuth, setUser, user } = useAuth();
  const location = useLocation();
  const referralCode = new URLSearchParams(location.search).get('ref');
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: User Account
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    
    // Step 2: Business Setup (will be handled separately)
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [referrerName, setReferrerName] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const navigate = useNavigate();

  // Save registration progress to localStorage
  const saveProgress = useCallback(() => {
    const token = localStorage.getItem('token');
    // Don't save if user is logged in (registration is complete)
    if (token) {
      return;
    }
    
    const progress = {
      currentStep,
      formData,
      userId,
      verificationStep,
      referralCode
    };
    localStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(progress));
  }, [currentStep, formData, userId, verificationStep, referralCode]);

  // Load registration progress from localStorage
  const loadProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(REGISTRATION_STORAGE_KEY);
      if (saved) {
        const progress = JSON.parse(saved);
        return progress;
      }
    } catch (error) {}
    return null;
  }, []);

  // Clear registration progress
  const clearProgress = useCallback(() => {
    localStorage.removeItem(REGISTRATION_STORAGE_KEY);
  }, []);

  // Check if user is logged in and redirect accordingly
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    // If user is logged in as supplier, check their status
    if (token && userType === 'supplier' && user) {
      // User is logged in - they should go to dashboard
      // Dashboard will handle showing NoBusinessAccess if needed
      navigate('/supplier/dashboard', { replace: true });
      return;
    }
    
    // If not logged in, try to restore progress
    const savedProgress = loadProgress();
    if (savedProgress) {
      setCurrentStep(savedProgress.currentStep || 0);
      setFormData(savedProgress.formData || {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
      });
      setUserId(savedProgress.userId || null);
      setVerificationStep(savedProgress.verificationStep || false);
      
      // If we have a userId and are on verification step, we're waiting for email verification
      if (savedProgress.userId && savedProgress.verificationStep) {
        setCurrentStep(1);
      }
    }
  }, [user, navigate, loadProgress]);

  // Save progress whenever relevant state changes
  useEffect(() => {
    saveProgress();
  }, [saveProgress]);

  // Fetch referral config when referral code is present
  useEffect(() => {
    const fetchReferralConfig = async () => {
      if (!referralCode) {
        setReferrerName(null);
        return;
      }

      try {
        setLoadingReferral(true);
        const response = await api.get(`/api/referrals/config/${referralCode}`);
        if (response.data && response.data.referrerName) {
          setReferrerName(response.data.referrerName);
        }
      } catch (error) {
        // Silently fail - referral code might not exist, that's okay
        setReferrerName(null);
      } finally {
        setLoadingReferral(false);
      }
    };

    fetchReferralConfig();
  }, [referralCode]);

  const steps = [
    'Create Account',
    'Verify Email',
    'Setup Business'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate required fields for step 1
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await api.post('/api/supplier/register', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        referralCode: referralCode || null
      });

      // Check if registration requires email verification
      if (response.data.userId) {
        setUserId(response.data.userId);
        setVerificationStep(true);
        setCurrentStep(1);
        setSuccessMessage(response.data.message || 'Please check your email for verification code');
        
        // Save progress after step 1 completion
        saveProgress();
        
        // If email wasn't sent successfully, show a more prominent error
        if (response.data.emailSent === false) {
          setError('We could not send the verification email. Please use the resend option below.');
        }
        
        return;
      }

      // If no verification required (should not happen with our new implementation)
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', 'supplier');

      navigate('/supplier/dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/supplier/verify-email', {
        userId,
        code: verificationCode
      });

      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userType', 'supplier');

      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      // Set user data directly in AuthContext
      setUser(response.data.user);

      // Clear verification state
      setVerificationStep(false);
      setVerificationCode('');
      setCurrentStep(2);

      // Clear registration progress since user is now logged in
      clearProgress();

      // Navigate to dashboard
      navigate('/supplier/dashboard', { replace: true });
    } catch (error) {
      setError(error.response?.data?.message || 'Error verifying email');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError('');

      await api.post('/api/supplier/resend-email-verification', {
        userId
      });

      setSuccessMessage('A new verification code has been sent to your email');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      setError(
        error.response?.data?.message ||
        error.message ||
        'Failed to resend verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  required
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  required
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  required
                  variant="outlined"
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  border: '1px solid #000',
                  color: 'black'
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
            </Box>
          </form>
        );
      
      case 1:
        return (
          <form onSubmit={handleVerificationSubmit}>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              We've sent a verification code to <strong>{formData.email}</strong>
            </Typography>
            
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              variant="outlined"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
              required
            />
            
            <Stack spacing={2} alignItems="center">
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  minWidth: 200,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  border: '1px solid #000',
                  color: 'black'
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify Email'}
              </Button>
              
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={loading}
                sx={{ color: 'black' }}
              >
                Resend Code
              </Button>
            </Stack>
          </form>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'secondary.main', // Lavender background
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '40px' }} />
      </Box>

      {/* Register Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '500px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 3
            }}
          >
            Supplier Registration
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Join Idotoo as a supplier and start managing your business
          </Typography>
          
          {/* Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {referralCode && (loadingReferral || referrerName) && (
            <Alert 
              icon={false}
              sx={{ 
                mb: 3,
                backgroundColor: '#fff3f8',
                border: '1px solid #ffb3d9',
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                  textAlign: 'center'
                }
              }}
            >
              {loadingReferral ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : referrerName ? (
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  💕 The Amazing <strong style={{ color: '#e91e63' }}>{referrerName}</strong> has invited you to become part of the idotoo community! 💕
                </Typography>
              ) : null}
            </Alert>
          )}
          
          {renderStepContent()}
          
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link href="/supplier/login" underline="hover" sx={{ color: 'black' }}>
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default SupplierRegister; 